import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button, Input, Select, Skeleton } from '../../components/ui'
import { ServiceCollection } from '../../features/catalog'
import { useGetCategoriesQuery, useGetServicesQuery } from '../../services/catalogApi'
import { useTenant } from '../../tenant/useTenant'

/** The category tab treatment from the Stitch services menu. */
function tabClass(active: boolean): string {
  return active
    ? 'whitespace-nowrap border-b-2 border-primary pb-1 font-label-caps text-label-caps font-bold uppercase text-primary transition-all'
    : 'whitespace-nowrap pb-1 font-label-caps text-label-caps uppercase text-on-surface-variant transition-colors hover:text-primary'
}

const PER_PAGE = 12
const SEARCH_DEBOUNCE_MS = 350

/** Query-string keys, kept in one place because both the reader and the writer
 *  below have to agree on them. */
type FilterKey = 'category' | 'branch' | 'q' | 'page'

function readNumber(value: string | null): number | null {
  if (!value) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

/**
 * E4 — the services menu. Every filter lives in the URL: a filtered list is a
 * link a customer can send to a friend, and the back button restores it.
 */
export function ServicesPage() {
  const { slug, bootstrap } = useTenant()
  const [params, setParams] = useSearchParams()

  const categoryId = readNumber(params.get('category'))
  const branchId = readNumber(params.get('branch'))
  const qParam = params.get('q') ?? ''
  const page = readNumber(params.get('page')) ?? 1

  const [term, setTerm] = useState(qParam)

  const update = useCallback(
    (patch: Partial<Record<FilterKey, string | null>>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          for (const [key, value] of Object.entries(patch)) {
            if (value === null || value === '') next.delete(key)
            else next.set(key, value)
          }
          return next
        },
        // Filtering is not a navigation step: it should not stack up in history.
        { replace: true },
      )
    },
    [setParams],
  )

  // Back/forward navigation owns the field, not the other way round.
  useEffect(() => {
    setTerm(qParam)
  }, [qParam])

  useEffect(() => {
    if (term === qParam) return
    const timer = setTimeout(() => update({ q: term || null, page: null }), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [term, qParam, update])

  const categories = useGetCategoriesQuery(slug)
  const services = useGetServicesQuery({ slug, categoryId, branchId, q: qParam || undefined, page, perPage: PER_PAGE })

  const branches = bootstrap.branches
  // One branch means the filter can only ever say the same thing.
  const showBranchFilter = branches.length > 1
  const hasFilters = categoryId !== null || branchId !== null || qParam !== ''
  const lastPage = services.data?.lastPage ?? 1

  return (
    <div className="container-page py-section-gap-md md:py-section-gap-lg">
      <header className="mb-16 max-w-2xl md:mb-24">
        <h1 className="mb-4 font-display text-headline-lg-mobile text-primary md:text-headline-lg">Curated services</h1>
        <p className="font-body-lg text-body-lg text-on-surface-variant">
          Discover our treatments, designed to rejuvenate and restore. Select a service to begin your booking.
        </p>
      </header>

      <div className="mb-12 flex flex-col gap-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            label="Search services"
            type="search"
            value={term}
            leadingIcon="search"
            placeholder="Try haircut or facial"
            onChange={(e) => setTerm(e.target.value)}
          />
          {showBranchFilter && (
            <Select
              label="Location"
              value={branchId === null ? '' : String(branchId)}
              onChange={(e) => update({ branch: e.target.value || null, page: null })}
            >
              <option value="">All locations</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          )}
        </div>

        {hasFilters && (
          <div className="flex items-center gap-4">
            <span className="font-body-md text-body-md text-on-surface-variant">
              {services.data
                ? `${services.data.total} ${services.data.total === 1 ? 'service' : 'services'}`
                : 'Filtering…'}
            </span>
            <Button
              variant="text"
              size="sm"
              onClick={() => update({ category: null, branch: null, q: null, page: null })}
            >
              Clear filters
            </Button>
          </div>
        )}
      </div>

      {/* Stitch's category rail: an underlined tab row that sticks under the
          header, not a chip cloud. It drives the same `category` query param. */}
      {categories.isLoading ? (
        <div className="mb-12 flex gap-8 border-b border-primary/10 py-4">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} shape="rect" className="h-5 w-24" />
          ))}
        </div>
      ) : categories.data?.length ? (
        <nav
          aria-label="Service categories"
          className="sticky top-[72px] z-40 md:top-[80px] -mx-margin-mobile mb-12 border-b border-primary/10 bg-surface/80 px-margin-mobile backdrop-blur-md md:-mx-gutter md:px-gutter"
        >
          <div className="flex items-center gap-8 overflow-x-auto py-4 no-scrollbar">
            <button type="button" className={tabClass(categoryId === null)} onClick={() => update({ category: null, page: null })}>
              All
            </button>
            {categories.data.map((category) => (
              <button
                key={category.id}
                type="button"
                className={tabClass(categoryId === category.id)}
                onClick={() =>
                  update({ category: categoryId === category.id ? null : String(category.id), page: null })
                }
              >
                {category.name}
              </button>
            ))}
          </div>
        </nav>
      ) : null}

      <ServiceCollection
        variant="list"
        grouped={categoryId === null}
        skeletonCount={5}
        services={services.data?.items ?? []}
        isLoading={services.isLoading}
        error={services.error}
        onRetry={services.refetch}
        emptyTitle={hasFilters ? 'No services match your filters' : 'No services yet'}
        emptyBody={
          hasFilters
            ? 'Try removing a filter or searching for something else.'
            : 'The salon is updating its service menu. Check back soon.'
        }
        emptyAction={
          hasFilters ? (
            <Button variant="outlined" onClick={() => update({ category: null, branch: null, q: null, page: null })}>
              Clear filters
            </Button>
          ) : undefined
        }
      />

      {lastPage > 1 && (
        <nav aria-label="Pagination" className="mt-16 flex items-center justify-center gap-6">
          <Button
            variant="outlined"
            disabled={page <= 1 || services.isFetching}
            onClick={() => update({ page: page <= 2 ? null : String(page - 1) })}
          >
            Previous
          </Button>
          <span className="font-body-md text-body-md text-on-surface-variant">
            Page {page} of {lastPage}
          </span>
          <Button
            variant="outlined"
            disabled={page >= lastPage || services.isFetching}
            onClick={() => update({ page: String(page + 1) })}
          >
            Next
          </Button>
        </nav>
      )}
    </div>
  )
}
